import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ViewMode } from '../../../../models';
import { DynamicReviewsExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import {
  CAROUSEL_DEFAULT_TITLE,
  LIST_DEFAULT_TITLE,
  DEFAULT_REVIEWS_TO_DISPLAY,
  DEFAULT_REVIEWS_PER_PAGE,
} from '../../../extension-components/dynamic-reviews/model';

const VISIBLE_CAROUSEL_REVIEWS = 1;

const CORNER_RADIUS_CSS: Record<string, string> = {
  none: '0px',
  small: '2px',
  base: '5px',
  large: '10px',
};

const PADDING_CSS: Record<string, string> = {
  none: '0px',
  small300: '7px',
  base: '14px',
  large200: '21px',
};

const BORDER_STYLE_CSS: Record<string, string> = {
  none: 'none',
  base: 'solid',
  dotted: 'dotted',
};

const BORDER_WIDTH_CSS: Record<string, string> = {
  base: '1px',
  medium: '2px',
};

const BACKGROUND_COLOR_CSS: Record<string, string> = {
  transparent: 'transparent',
  base: 'var(--surface-container-default)',
  subdued: 'var(--surface-container-high)',
};

const SAMPLE_PRODUCTS = [
  {
    name: 'Premium Wireless Headphones',
    price: '$9.99',
    comparePrice: '$5.99',
    variant: 'Variant selection',
    rating: 4.8,
    reviewCount: 124,
  },
  { name: 'Organic Cotton T-Shirt', price: '$9.99', comparePrice: '$5.99', variant: '', rating: 4.5, reviewCount: 89 },
];

const SAMPLE_REVIEWS: DynamicReviewsExtension.ParsedReview[] = [
  {
    productId: '1',
    productName: 'Premium Wireless Headphones',
    reviewText:
      'Sound quality is incredible and the noise cancellation works perfectly. Super comfortable for long listening sessions.',
    reviewStarRating: 5,
    reviewerName: 'Emily R.',
  },
  {
    productId: '2',
    productName: 'Organic Cotton T-Shirt',
    reviewText: 'Really soft fabric and fits true to size. Already ordered two more in different colors!',
    reviewStarRating: 4.5,
    reviewerName: 'Veronica D.',
  },
  {
    productId: '3',
    productName: 'Stainless Steel Water Bottle',
    reviewText: 'Battery life is amazing, easily lasts through my whole work day. Best headphones I have owned.',
    reviewStarRating: 5,
    reviewerName: 'Michael T.',
  },
  {
    productId: '4',
    productName: 'Running Shoes Pro',
    reviewText: 'Great quality for the price. Held up well after several washes and still looks brand new.',
    reviewStarRating: 4,
    reviewerName: 'David K.',
  },
];

@Component({
  selector: 'dynamic-reviews-example',
  templateUrl: './dynamic-reviews-example.component.html',
  styleUrls: ['./dynamic-reviews-example.component.scss'],
  standalone: false,
})
export class DynamicReviewsExampleComponent implements OnChanges {
  @Input() template: DynamicReviewsExtension.Template;
  @Input() mode: ViewMode = null;
  @Input() isMobile: boolean = false;

  LayoutStyle = DynamicReviewsExtension.LayoutStyle;
  RatingBadgeType = DynamicReviewsExtension.RatingBadgeType;
  CarouselNavigationStyle = DynamicReviewsExtension.CarouselNavigationStyle;
  StarPosition = DynamicReviewsExtension.StarPosition;

  sampleProducts = SAMPLE_PRODUCTS;
  sampleReviews = SAMPLE_REVIEWS;
  listPage = 0;
  visibleListReviews: DynamicReviewsExtension.ParsedReview[] = [];
  paginatedListReviews: DynamicReviewsExtension.ParsedReview[] = [];
  totalListPages = 0;

  get layoutStyle(): DynamicReviewsExtension.LayoutStyle {
    return this.template?.layoutStyle || DynamicReviewsExtension.LayoutStyle.RATING_BADGE;
  }

  get ratingBadgeType(): DynamicReviewsExtension.RatingBadgeType {
    return this.template?.ratingBadgeConfig?.type || DynamicReviewsExtension.RatingBadgeType.STARS;
  }

  get starColor(): string {
    switch (this.layoutStyle) {
      case DynamicReviewsExtension.LayoutStyle.RATING_BADGE:
        return this.template?.ratingBadgeConfig?.starColor || DynamicReviewsExtension.DEFAULT_STAR_COLOR;
      case DynamicReviewsExtension.LayoutStyle.REVIEW_CAROUSEL:
        return this.template?.carouselConfig?.design?.starColor || DynamicReviewsExtension.DEFAULT_STAR_COLOR;
      default:
        return this.template?.listConfig?.design?.starColor || DynamicReviewsExtension.DEFAULT_STAR_COLOR;
    }
  }

  get carouselCardStyles(): Record<string, string> {
    return this.mapDesignToStyles(this.template?.carouselConfig?.design);
  }

  get listItemStyles(): Record<string, string> {
    return this.mapDesignToStyles(this.template?.listConfig?.design);
  }

  get carouselTitle(): string {
    return this.template?.carouselConfig?.title || CAROUSEL_DEFAULT_TITLE;
  }

  get listTitle(): string {
    return this.template?.listConfig?.title || LIST_DEFAULT_TITLE;
  }

  get starPosition(): DynamicReviewsExtension.StarPosition {
    return this.template?.carouselConfig?.starPosition ?? DynamicReviewsExtension.StarPosition.TOP;
  }

  get isScrollbarNav(): boolean {
    return this.template?.carouselConfig?.navigationStyle === DynamicReviewsExtension.CarouselNavigationStyle.SCROLLBAR;
  }

  get visibleCarouselReviews(): DynamicReviewsExtension.ParsedReview[] {
    if (this.isScrollbarNav) {
      return this.sampleReviews;
    }
    return this.sampleReviews.slice(0, VISIBLE_CAROUSEL_REVIEWS);
  }

  get carouselDescription(): string {
    return this.getCleanContent(this.template?.carouselConfig?.description);
  }

  get listDescription(): string {
    return this.getCleanContent(this.template?.listConfig?.description);
  }

  getRenderedCardContent(review: DynamicReviewsExtension.ParsedReview): string {
    return this.renderContent(this.carouselDescription, review);
  }

  getRenderedListContent(review: DynamicReviewsExtension.ParsedReview): string {
    return this.renderContent(this.listDescription, review);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['template']) {
      this.listPage = 0;
      this.recomputeListProperties();
    }
  }

  listPagePrev() {
    if (this.listPage > 0) {
      this.listPage--;
      this.recomputeListProperties();
    }
  }

  listPageNext() {
    if (this.listPage < this.totalListPages - 1) {
      this.listPage++;
      this.recomputeListProperties();
    }
  }

  private recomputeListProperties(): void {
    const reviewsToDisplay = this.template?.reviewsToDisplay || DEFAULT_REVIEWS_TO_DISPLAY;
    const perPage = this.template?.listConfig?.reviewsPerPage || DEFAULT_REVIEWS_PER_PAGE;
    this.visibleListReviews = this.sampleReviews.slice(0, reviewsToDisplay);
    this.totalListPages = Math.ceil(this.visibleListReviews.length / perPage);
    const start = this.listPage * perPage;
    this.paginatedListReviews = this.visibleListReviews.slice(start, start + perPage);
  }

  private mapDesignToStyles(design?: DynamicReviewsExtension.DesignOptions): Record<string, string> {
    if (!design) return {};
    const borderStyle = BORDER_STYLE_CSS[design.border] || 'none';
    const borderWidth = borderStyle !== 'none' ? BORDER_WIDTH_CSS[design.borderWidth] || '1px' : '0';
    return {
      'background-color': BACKGROUND_COLOR_CSS[design.backgroundColor] || 'transparent',
      border: borderStyle !== 'none' ? `${borderWidth} ${borderStyle} #ccc` : 'none',
      'border-radius': CORNER_RADIUS_CSS[design.cornerRadius] || '5px',
      padding: PADDING_CSS[design.padding] || '14px',
    };
  }

  private getCleanContent(content: string): string {
    if (!content || !content.replace(/<[^>]*>/g, '').trim()) {
      return '';
    }
    return content;
  }

  private buildDynamicFields(review: DynamicReviewsExtension.ParsedReview): Record<string, string> {
    return {
      '{rating_number}': review.reviewStarRating != null ? String(review.reviewStarRating) : '',
      '{review_text}': review.reviewText || '',
      '{reviewer_name}': review.reviewerName || '',
      '{product_name}': review.productName || '',
    };
  }

  private renderContent(content: string, review: DynamicReviewsExtension.ParsedReview): string {
    const fields = this.buildDynamicFields(review);
    return Object.entries(fields).reduce((result, [key, value]) => result.split(key).join(value), content);
  }

  getStarsArray(rating: number): number[] {
    return Array(5)
      .fill(0)
      .map((_, i) => (i < Math.floor(rating) ? 1 : i < rating ? 0.5 : 0));
  }

  formatRating(rating: number): string {
    return rating.toFixed(1);
  }
}
